import { Box, Paper, Typography } from '@mui/material'
import { AdvancedMarker, APIProvider, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps'
import { t } from 'i18next'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'

import { GOOGLE_MAPS_API_KEY } from '../../../configs/environment.js'

const MAP_ID = 'device-position-history-map'
const TIMEZONE = 'Europe/Rome'
const FALLBACK_CENTER = { lat: 0, lng: 0 }
const FALLBACK_ZOOM = 3
const SINGLE_POINT_ZOOM = 16
const MAX_FIT_ZOOM = 18

const isValidCoordinate = (value, limit) => typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= limit

const toValidPoints = (positions) => {
  const points = (Array.isArray(positions) ? positions : [])
    .filter((p) => isValidCoordinate(p?.latitude, 90) && isValidCoordinate(p?.longitude, 180))
    .map((p) => ({ lat: p.latitude, lng: p.longitude, t: p.t, time: Date.parse(p.t) }))

  // l'ordine per timestamp e' applicato solo se tutti i campioni hanno un timestamp valido
  if (points.every((p) => Number.isFinite(p.time))) {
    points.sort((a, b) => a.time - b.time)
  }

  return points
}

const formatTimestamp = (value) => {
  const m = moment(value)
  return m.isValid() ? m.tz(TIMEZONE).format('DD/MM/YYYY HH:mm:ss') : undefined
}

const Track = ({ points }) => {
  const map = useMap()
  const [selectedIndex, setSelectedIndex] = useState(null)

  useEffect(() => setSelectedIndex(null), [points])

  useEffect(() => {
    if (!map) {
      return
    }

    const clickListener = map.addListener('click', () => setSelectedIndex(null))
    return () => clickListener.remove()
  }, [map])

  useEffect(() => {
    if (!map || points.length === 0) {
      return
    }

    const first = points[0]
    const allCoincident = points.every((p) => p.lat === first.lat && p.lng === first.lng)

    if (allCoincident) {
      map.setCenter({ lat: first.lat, lng: first.lng })
      map.setZoom(SINGLE_POINT_ZOOM)
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }))
    map.fitBounds(bounds, 40)

    const idleListener = map.addListener('idle', () => {
      idleListener.remove()
      if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM)
    })

    return () => idleListener.remove()
  }, [map, points])

  useEffect(() => {
    if (!map || points.length < 2) {
      return
    }

    const polyline = new window.google.maps.Polyline({
      map,
      path: points.map((p) => ({ lat: p.lat, lng: p.lng })),
      strokeColor: '#0f81fc',
      strokeOpacity: 0.8,
      strokeWeight: 3
    })

    return () => polyline.setMap(null)
  }, [map, points])

  const selected = selectedIndex === null ? null : points[selectedIndex]

  return (
    <>
      {points.map((p, index) => (
        <AdvancedMarker
          key={index}
          position={{ lat: p.lat, lng: p.lng }}
          zIndex={index === selectedIndex ? points.length : index}
          onClick={() => setSelectedIndex(index)}
        >
          <div
            style={{
              minWidth: 24,
              height: 24,
              boxSizing: 'border-box',
              padding: '0 4px',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0f81fc',
              color: 'white',
              fontSize: 11,
              fontWeight: 'bold',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
              cursor: 'pointer'
            }}
          >
            {index + 1}
          </div>
        </AdvancedMarker>
      ))}

      {selected && (
        <InfoWindow
          position={{ lat: selected.lat, lng: selected.lng }}
          pixelOffset={[0, -18]}
          onCloseClick={() => setSelectedIndex(null)}
        >
          <Typography variant="body2" color="text.primary" sx={{ color: '#000' }}>
            <strong>#{selectedIndex + 1}</strong>
            {formatTimestamp(selected.t) && ` — ${formatTimestamp(selected.t)}`}
          </Typography>
        </InfoWindow>
      )}
    </>
  )
}

const PositionHistoryMap = ({ positions, isLoading, initialPosition }) => {
  const points = useMemo(() => toValidPoints(positions), [positions])

  const hasInitialPosition = isValidCoordinate(initialPosition?.latitude, 90) && isValidCoordinate(initialPosition?.longitude, 180)
  const defaultCenter = hasInitialPosition
    ? { lat: initialPosition.latitude, lng: initialPosition.longitude }
    : FALLBACK_CENTER
  const defaultZoom = hasInitialPosition ? SINGLE_POINT_ZOOM : FALLBACK_ZOOM

  return (
    <Box mt={4} role="region" aria-label={t('devices.positionHistoryTitle')}>
      <Typography variant="h5" fontWeight="bold" mb={1}>
        {t('devices.positionHistoryTitle')}
      </Typography>

      <Box sx={{ position: 'relative', width: '100%', height: 360 }}>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY} language="it" region="IT">
          <Map
            id={MAP_ID}
            mapId={MAP_ID}
            style={{ width: '100%', height: '100%' }}
            defaultCenter={defaultCenter}
            defaultZoom={defaultZoom}
            gestureHandling="greedy"
            zoomControl
            disableDefaultUI
          >
            <Track points={points} />
          </Map>
        </APIProvider>

        {(isLoading || points.length === 0) && (
          <Paper
            elevation={2}
            sx={{ position: 'absolute', top: 8, left: 8, px: 1.5, py: 0.5, pointerEvents: 'none' }}
          >
            <Typography variant="body2">
              {isLoading ? t('devices.positionHistoryLoading') : t('devices.positionHistoryEmpty')}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default PositionHistoryMap
